'use client';
import { Modal, SemiBold24 } from '@/components/design-system';
import styles from './dashboard.module.scss';
import DashboardMapper from './mapper';
import AnimatedDiv from '@/components/animated/animated';
import { useDashboard } from '@/contexts/dashboard/dashboard.context';
import OverlayLoading from '../../(motor)/motor/renewal/[policyNo]/components/overlay-loading/overlayLoading';
import { useGeneralContext } from '@/contexts/general/general.context';
import {
  getDesktopSectionsFrom,
  getMobileSectionsFrom,
} from '@/core/utils/misc.utils';
import { useModal } from '@/hooks/useModal';
import { useEffect, useRef, useState } from 'react';
import StatusModal from '@/components/status-modal/statusModal';
import {
  formatter,
  ModalContentType,
  modalFormatter,
  getEmiRecoveryNotifier,
} from './helper';
import GenericLoader from '../../components/loader/genericLoader';
import { getImageProps } from 'next/image';
import { getBackgroundImage } from '@/core/utils/image.utils';
import { getSalutation } from '@/data/api/policy/policy.api';
import Image from 'next/image';
import { getProposerName } from '@/core/utils/policy.util';
import { checkHereAppLogin } from '@/data/api/auth/auth.api';
import { ACTION_MODAL_TYPE } from '@/core/constants/quickAction.constants';
import { useClevertap } from '@/contexts/analytics/analytics.context';
import { setUIState } from '@/stores/ui.store';

export default function Dashboard() {
  const {
    downloadPolicyStatus,
    dashboardLabels,
    downloadPolicyLabels,
    dashboardModel,
    setDashboardModel,
    setShowBanner,
    setEmiRecoveryNotifier,
  } = useDashboard();
  const sections = formatter(dashboardLabels.alignment);
  const desktopSectionsObj = getDesktopSectionsFrom(sections);
  const mobileSectionsObj = getMobileSectionsFrom(sections);
  const { policies, claims, profileData } = useGeneralContext();
  const [redCircle, setRedCircle] = useState<boolean>(false);
  const handleModalData = modalFormatter(
    dashboardLabels,
    downloadPolicyLabels,
    dashboardModel
  );
  const [modalData, setModalData] = useState<ModalContentType>({});
  const hasFetchedEmi = useRef(false);
  const { showModal, closeModal, ref } = useModal({
    onOutsideClick: ({ closeModal }) => {
      closeModal();
    },
  });
  const clevertap = useClevertap();

  async function getSalutationData() {
    getSalutation()
      .then((res) => {
        setRedCircle(res.data.redCircle);
      })
      .catch(() => {});
  }

  async function checkBanner() {
    const resp = await checkHereAppLogin(profileData.value?.mobile ?? '');
    setShowBanner(!resp);
  }

  useEffect(() => {
    getSalutationData();
  }, []);

  useEffect(() => {
    if (downloadPolicyStatus === 'failure') {
      setModalData(handleModalData(ACTION_MODAL_TYPE.whatsapp));
      showModal();
    } else if (downloadPolicyStatus === 'error') {
      setModalData(handleModalData(ACTION_MODAL_TYPE.error));
      showModal();
    }
  }, [downloadPolicyStatus]);

  useEffect(() => {
    if (dashboardModel?.action) {
      setModalData(handleModalData(dashboardModel.action));
      showModal();
    }
  }, [dashboardModel?.action]);

  useEffect(() => {
    if (profileData.value?.mobile) {
      checkBanner();
    }
  }, [profileData]);

  useEffect(() => {
    if (!policies.value) {
      return;
    }

    async function getEmiRecoveryNotifierData() {
      if (hasFetchedEmi.current) return;
      hasFetchedEmi.current = true;

      if (policies.value) {
        const res = await getEmiRecoveryNotifier(policies.value);
        setEmiRecoveryNotifier(res);
      }
    }
    getEmiRecoveryNotifierData();
  }, [policies.value]);

  useEffect(() => {
    if (clevertap) {
      clevertap.sendEventWithName('Dashboard_Viewed', {});
      document.addEventListener('CT_web_native_display', function (event: any) {
        const data = event?.detail;
        const topic = data?.kv?.topic;
        switch (topic) {
          case 'Test Native Display': {
            setUIState('native-display-banner-1', data);
            break;
          }
        }
      });
    }
  }, [clevertap]);

  function onCloseModal() {
    setDashboardModel({
      action: null,
    });
    closeModal();
  }

  const {
    props: { srcSet: mobileSrcSet },
  } = getImageProps({
    alt: '',
    width: 768,
    height: 1528,
    src: '/images/dashboard/dashboard_bgre.webp',
    quality: 50,
  });

  const {
    props: { srcSet },
  } = getImageProps({
    alt: '',
    width: 1780,
    height: 3528,
    src: '/images/dashboard/dashboard_bgre.webp',
    quality: 50,
  });

  const backgroundImage = getBackgroundImage(mobileSrcSet);
  const backgroundImageDesktop = getBackgroundImage(srcSet);

  if (policies.loading || claims.loading) {
    return <GenericLoader />;
  }
  return (
    <>
      {downloadPolicyStatus === 'loading' && <OverlayLoading />}
      <Modal ref={ref} closeCallback={onCloseModal}>
        <StatusModal
          title={modalData.title}
          description={modalData.description}
          onPrimaryButtonClick={onCloseModal}
          primaryButtonText={modalData.buttonText}
          imageUrl={modalData.imageUrl}
          queryWidget={{
            contactMail: modalData.contactMail,
            contactText: modalData.contactText,
          }}
        />
      </Modal>
      <div
        className={styles['dashboard-page-main-con']}
        style={
          {
            '--_background-image': backgroundImage,
            '--_background-image-desktop': backgroundImageDesktop,
          } as React.CSSProperties
        }
      >
        <div className={styles['parent-con']}>
          <AnimatedDiv.Opacity className={styles['name-con']}>
            <SemiBold24>{getProposerName(policies.value)}</SemiBold24>
            {redCircle ? (
              <Image
                src="/images/dashboard/redCircle.png"
                alt="redCircle"
                width={75}
                height={28}
                className={styles['red-circle']}
              />
            ) : null}
          </AnimatedDiv.Opacity>
          <div className={styles['desktop-main-con']}>
            <div className={styles['left']}>
              {desktopSectionsObj?.left.map((a) => (
                <DashboardMapper type={a.type} key={a.type} />
              ))}
            </div>
            <div className={styles['right']}>
              {desktopSectionsObj?.right.map((a) => (
                <DashboardMapper type={a.type} key={a.type} />
              ))}
            </div>
          </div>
          <div className={styles['mobile-main-con']}>
            <div className={styles['common']}>
              {mobileSectionsObj?.map((a) => (
                <DashboardMapper type={a.type} key={a.type} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
